import React, { useState, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { CONFIG } from '../config/variables';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiCheck, FiAlertCircle, FiLoader, FiShield } = FiIcons;

/*
  Legacy filename retained because this component is now the enquiry form mounted at #payment.
  This file handles the lead/contact form submission via hidden iframe POST to Google Apps Script.
*/

// Custom Dropdown Component with dark styling
const CustomDropdown = ({ label, name, value, onChange, options, placeholder, required }) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);

  React.useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const selectedLabel = options.find(opt => opt.value === value)?.label || placeholder;

  return (
    <div className="relative" ref={dropdownRef}>
      <label className="block text-slate-200 font-semibold text-sm mb-2">{label}{required && ' *'}</label>
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="w-full bg-slate-900/80 border border-slate-700/80 rounded-xl px-4 py-3 text-left text-white hover:bg-slate-800/80 transition-all flex items-center justify-between h-12 focus:border-blue-400 focus:ring-2 focus:ring-blue-500/30 outline-none"
      >
        <span className={value ? 'text-white' : 'text-slate-500'}>{selectedLabel}</span>
        <SafeIcon icon={FiIcons.FiChevronDown} className={`w-4 h-4 text-slate-400 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </button>
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.15 }}
            className="absolute z-50 w-full mt-2 bg-slate-900 border border-slate-700/80 rounded-xl shadow-2xl max-h-60 overflow-y-auto"
          >
            {options.map((opt) => (
              <button
                key={opt.value}
                type="button"
                onClick={() => {
                  onChange({ target: { name, value: opt.value } });
                  setIsOpen(false);
                }}
                className={`w-full px-4 py-3 text-left text-sm hover:bg-slate-800/80 transition-colors ${value === opt.value ? 'bg-blue-500/20 text-blue-300' : 'text-white'}`}
              >
                {opt.label}
              </button>
            ))}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

// Staged Submission Modal Component
const SubmissionModal = ({ stage, isVisible }) => {
  const stages = [
    { text: 'Receiving your landing page enquiry', icon: FiLoader },
    { text: 'Recording your business goals and service area', icon: FiLoader },
    { text: 'Notifying the BPA Solutions team', icon: FiLoader },
    { text: 'Preparing your confirmation email and SMS', icon: FiLoader }
  ];

  const finalStage = { text: 'Enquiry received — confirmation prepared', icon: FiCheck };

  return (
    <AnimatePresence>
      {isVisible && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[100] flex items-center justify-center bg-black/80 backdrop-blur-md"
        >
          <motion.div
            initial={{ scale: 0.95, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.95, opacity: 0 }}
            className="bg-gradient-to-br from-slate-900 to-black border border-slate-700/50 rounded-2xl p-8 max-w-md mx-4 text-center"
          >
            <div className="mb-6">
              {stage < stages.length ? (
                <SafeIcon icon={stages[stage].icon} className="w-12 h-12 text-blue-400 animate-spin mx-auto" />
              ) : (
                <div className="w-12 h-12 bg-green-500/20 rounded-full flex items-center justify-center mx-auto">
                  <SafeIcon icon={FiCheck} className="w-6 h-6 text-green-400" />
                </div>
              )}
            </div>
            <h3 className="text-xl font-bold text-white mb-2">
              {stage < stages.length ? stages[stage].text : finalStage.text}
            </h3>
            <p className="text-slate-400 text-sm">Please keep this page open for a moment.</p>
            {stage >= stages.length && (
              <p className="text-green-300 text-sm mt-4">Thanks. Your enquiry has been received and BPA Solutions has been notified.</p>
            )}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

const Payment = () => {
  const [formData, setFormData] = useState({
    fullName: '',
    businessName: '',
    phone: '',
    email: '',
    tradeType: '',
    serviceArea: '',
    currentWebsite: '',
    mainGoal: '',
    preferredPreview: '',
    preferredContactDate: '',
    bestTimeToCall: '',
    message: '',
    consentToContact: false
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const [showStageModal, setShowStageModal] = useState(false);
  const [submitStage, setSubmitStage] = useState(0);
  const [leadId, setLeadId] = useState(null);
  
  // Refs for cleanup
  const formRef = useRef(null);
  const timeoutRef = useRef(null);
  const messageListenerRef = useRef(null);
  const iframeRef = useRef(null);
  const isProcessingRef = useRef(false);

  // Dropdown options
  const tradeOptions = [
    { value: '', label: 'Select your trade' },
    { value: 'Electrician', label: 'Electrician' },
    { value: 'Plumber', label: 'Plumber' },
    { value: 'Roofing', label: 'Roofing' },
    { value: 'Fencing', label: 'Fencing' },
    { value: 'Landscaping', label: 'Landscaping' },
    { value: 'Detailing', label: 'Mobile Detailing' },
    { value: 'Cleaning', label: 'Cleaning' },
    { value: 'Pressure Washing', label: 'Pressure Washing' },
    { value: 'Other', label: 'Other' }
  ];

  const goalOptions = [
    { value: '', label: 'Select your main goal' },
    { value: 'More phone calls', label: 'More phone calls' },
    { value: 'More quote requests', label: 'More quote requests' },
    { value: 'More booking enquiries', label: 'More booking enquiries' },
    { value: 'Better trust before customers call', label: 'Better trust before customers call' },
    { value: 'Replace or improve my current website', label: 'Replace or improve my current website' },
    { value: 'Not sure yet', label: 'Not sure yet' }
  ];

  const timeOptions = [
    { value: '', label: 'Select best time' },
    { value: 'Morning', label: 'Morning' },
    { value: 'Afternoon', label: 'Afternoon' },
    { value: 'Evening', label: 'Evening' },
    { value: 'Anytime', label: 'Anytime' }
  ];

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSubmissionSuccess = () => {
    // Clear timeout
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
    // Remove listener
    if (messageListenerRef.current) {
      window.removeEventListener('message', messageListenerRef.current);
      messageListenerRef.current = null;
    }
    isProcessingRef.current = false;
    setShowStageModal(false);
    setIsSubmitting(false);
    setIsSubmitted(true);
  };

  const handleSubmissionError = (errorMsg) => {
    // Clear timeout
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
    // Remove listener
    if (messageListenerRef.current) {
      window.removeEventListener('message', messageListenerRef.current);
      messageListenerRef.current = null;
    }
    isProcessingRef.current = false;
    setShowStageModal(false);
    setIsSubmitting(false);
    setSubmitError(errorMsg || 'We could not confirm the submission. Please check the lead sheet before resubmitting, or contact BPA Solutions directly.');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Prevent duplicate submissions
    if (isProcessingRef.current) return;
    isProcessingRef.current = true;
    
    setSubmitError(null);
    setIsSubmitting(true);
    setShowStageModal(true);
    setSubmitStage(0);

    // Generate submission nonce for verification
    const submissionNonce = `bpas-${Date.now()}-${Math.random().toString(36).slice(2)}`;

    // Stage progression
    const stageTimer = setInterval(() => {
      setSubmitStage(prev => {
        if (prev < 3) return prev + 1;
        return prev;
      });
    }, 700);

    const payload = {
      fullName: formData.fullName,
      businessName: formData.businessName || '',
      phone: formData.phone,
      email: formData.email,
      tradeType: formData.tradeType,
      serviceArea: formData.serviceArea,
      currentWebsite: formData.currentWebsite || '',
      mainGoal: formData.mainGoal,
      preferredPreview: '',
      preferredContactDate: formData.preferredContactDate || '',
      bestTimeToCall: formData.bestTimeToCall,
      message: formData.message || '',
      consentToContact: formData.consentToContact,
      sourcePage: window.location.href,
      submittedAt: new Date().toISOString(),
      submissionNonce: submissionNonce
    };

    const cmsUrl = CONFIG.BPAS_CMS_URL || CONFIG.FORM_SUBMIT_URL;

    if (!cmsUrl || cmsUrl === 'TOBEUPDATED') {
      clearInterval(stageTimer);
      handleSubmissionError('Enquiry submission is not connected yet. Please configure the CMS URL.');
      return;
    }

    // STEP 1: Create hidden iframe FIRST (must exist before form.submit())
    let iframe = document.getElementById('bpasLeadSubmitFrame');
    if (!iframe) {
      iframe = document.createElement('iframe');
      iframe.id = 'bpasLeadSubmitFrame';
      iframe.name = 'bpasLeadSubmitFrame';
      iframe.style.display = 'none';
      // NO sandbox attribute - allows postMessage communication
      document.body.appendChild(iframe);
    }
    iframeRef.current = iframe;

    // STEP 2: Create postMessage listener BEFORE submitting
    // This listener is fresh for THIS submission only
    const handleMessage = (event) => {
      // Only accept messages with our source identifier
      if (!event.data || event.data.source !== 'BPAS_LEAD_FORM') {
        return; // Ignore unrelated messages silently
      }

      // Accept from script.google.com or script.googleusercontent.com
      const allowedOrigins = [
        'https://script.google.com',
        'https://script.googleusercontent.com'
      ];
      if (event.origin && !allowedOrigins.some(origin => event.origin.startsWith(origin))) {
        // Still process if source matches - origin can vary
        console.debug('BPAS message from origin:', event.origin);
      }

      const responsePayload = event.data.payload;

      // Clear timeout immediately when response arrives
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
      clearInterval(stageTimer);

      // Verify nonce if provided (optional for backward compatibility)
      if (responsePayload.submissionNonce && responsePayload.submissionNonce !== submissionNonce) {
        // Nonce mismatch - ignore this message
        return;
      }

      // Check for success
      if (responsePayload.ok === true || responsePayload.success === true) {
        setLeadId(responsePayload.leadId || null);
        handleSubmissionSuccess();
      } else if (responsePayload.ok === false || responsePayload.success === false) {
        const errorMsg = responsePayload.message || 'Submission failed. Please try again.';
        handleSubmissionError(errorMsg);
      }
    };

    messageListenerRef.current = handleMessage;
    window.addEventListener('message', handleMessage);

    // STEP 3: Add iframe load listener for diagnostic only (NOT success)
    const handleIframeLoad = () => {
      // Iframe loaded but we still need postMessage for confirmation
      // This just extends patience slightly
      console.debug('Iframe loaded, awaiting postMessage confirmation...');
    };
    iframe.addEventListener('load', handleIframeLoad);

    // STEP 4: Create hidden form (must exist before form.submit())
    const form = document.createElement('form');
    form.method = 'POST';
    form.action = cmsUrl;
    form.target = 'bpasLeadSubmitFrame';
    form.style.display = 'none';

    // Add payload as hidden input
    const payloadInput = document.createElement('input');
    payloadInput.type = 'hidden';
    payloadInput.name = 'payload';
    payloadInput.value = JSON.stringify(payload);
    form.appendChild(payloadInput);

    document.body.appendChild(form);

    // STEP 5: Submit after DOM is ready
    requestAnimationFrame(() => {
      setTimeout(() => {
        form.submit();
        
        // Clean up hidden form after submission starts
        setTimeout(() => {
          if (document.body.contains(form)) {
            document.body.removeChild(form);
          }
        }, 1000);
      }, 50);
    });

    // STEP 6: Timeout after 25 seconds
    timeoutRef.current = setTimeout(() => {
      clearInterval(stageTimer);
      // Remove listener on timeout
      if (messageListenerRef.current) {
        window.removeEventListener('message', messageListenerRef.current);
        messageListenerRef.current = null;
      }
      // Remove iframe load listener
      iframe.removeEventListener('load', handleIframeLoad);
      handleSubmissionError('We could not confirm the submission. Please check the lead sheet before resubmitting, or contact BPA Solutions directly.');
    }, 25000);
  };

  if (isSubmitted) {
    return (
      <section id="payment" className="py-20 bg-gradient-to-br from-black to-slate-900">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="bg-green-500/10 border border-green-500/30 rounded-2xl p-6 sm:p-8 lg:p-10 text-center"
          >
            <div className="w-16 h-16 sm:w-20 sm:h-20 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-6">
              <SafeIcon icon={FiCheck} className="w-8 h-8 sm:w-10 sm:h-10 text-green-400" />
            </div>
            <h3 className="text-2xl sm:text-3xl font-bold text-white mb-4">
              Thanks — Your Enquiry Has Been Received
            </h3>
            <p className="text-slate-300 text-base sm:text-lg mb-6 max-w-2xl mx-auto">
              We have received your landing page enquiry and will review your business, service area and goals.
            </p>
            <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-4 sm:p-5 mb-6">
              <p className="text-blue-200 text-sm sm:text-base font-semibold mb-2">
                We normally respond between 8:00am and 6:00pm Sydney time
              </p>
              <p className="text-slate-400 text-sm">
                We will try to contact you during your nominated preferred time.
              </p>
              {formData.preferredContactDate && (
                <p className="text-slate-400 text-sm mt-2">
                  We will also take your preferred contact date into account.
                </p>
              )}
            </div>
            <div className="bg-slate-800/50 border border-slate-700/50 rounded-lg p-4 sm:p-5 text-left">
              <p className="text-slate-400 text-sm mb-3 font-semibold">What happens next:</p>
              <ul className="space-y-2 text-slate-300 text-sm">
                <li className="flex items-start gap-2">
                  <SafeIcon icon={FiCheck} className="text-green-400 w-4 h-4 flex-shrink-0 mt-0.5" />
                  <span>Your enquiry has been recorded</span>
                </li>
                <li className="flex items-start gap-2">
                  <SafeIcon icon={FiCheck} className="text-green-400 w-4 h-4 flex-shrink-0 mt-0.5" />
                  <span>BPA Solutions has been notified</span>
                </li>
                <li className="flex items-start gap-2">
                  <SafeIcon icon={FiCheck} className="text-green-400 w-4 h-4 flex-shrink-0 mt-0.5" />
                  <span>A confirmation message has been prepared for you</span>
                </li>
                <li className="flex items-start gap-2">
                  <SafeIcon icon={FiCheck} className="text-green-400 w-4 h-4 flex-shrink-0 mt-0.5" />
                  <span>We will attempt to call during your preferred window</span>
                </li>
              </ul>
            </div>
          </motion.div>
        </div>
      </section>
    );
  }

  return (
    <section id="payment" className="py-20 bg-gradient-to-br from-black to-slate-900">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
          className="text-center mb-10"
        >
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-white mb-4">
            Request My Landing Page Plan
          </h2>
          <p className="text-base sm:text-lg text-slate-400">
            Tell us about your business and what you want this page to achieve.
          </p>
        </motion.div>

        <motion.form
          ref={formRef}
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
          viewport={{ once: true }}
          onSubmit={handleSubmit}
          className="bg-slate-900/50 backdrop-blur-sm border border-slate-700/50 rounded-2xl p-5 sm:p-6 lg:p-8 space-y-4"
        >
          {submitError && (
            <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-4 flex items-start gap-3">
              <SafeIcon icon={FiAlertCircle} className="text-red-400 w-5 h-5 flex-shrink-0 mt-0.5" />
              <p className="text-red-300 text-sm">{submitError}</p>
            </div>
          )}

          <div className="grid md:grid-cols-2 gap-4">
            <Input label="Your name *" name="fullName" value={formData.fullName} onChange={handleInputChange} required />
            <Input label="Business name" name="businessName" value={formData.businessName} onChange={handleInputChange} required={false} />
          </div>

          <div className="grid md:grid-cols-2 gap-4">
            <Input label="Mobile number *" name="phone" value={formData.phone} onChange={handleInputChange} required type="tel" />
            <Input label="Email address *" name="email" value={formData.email} onChange={handleInputChange} required type="email" />
          </div>

          <div className="grid md:grid-cols-2 gap-4">
            <CustomDropdown
              label="Business type / trade"
              name="tradeType"
              value={formData.tradeType}
              onChange={handleInputChange}
              options={tradeOptions}
              placeholder="Select your trade"
              required
            />
            <Input label="Main service area or suburbs *" name="serviceArea" value={formData.serviceArea} onChange={handleInputChange} required placeholder="e.g. Western Sydney, Northern Beaches" />
          </div>

          <Input label="Current website, if any" name="currentWebsite" value={formData.currentWebsite} onChange={handleInputChange} placeholder="https://" />

          <div className="grid md:grid-cols-2 gap-4">
            <CustomDropdown
              label="What do you want this landing page to achieve?"
              name="mainGoal"
              value={formData.mainGoal}
              onChange={handleInputChange}
              options={goalOptions}
              placeholder="Select your main goal"
              required
            />
            <Input 
              label="Preferred contact date" 
              name="preferredContactDate" 
              value={formData.preferredContactDate} 
              onChange={handleInputChange} 
              type="date"
              required={false}
              className="text-white [&::-webkit-calendar-picker-indicator]:invert [&::-webkit-calendar-picker-indicator]:dark:invert"
            />
          </div>

          <CustomDropdown
            label="Best time to contact you?"
            name="bestTimeToCall"
            value={formData.bestTimeToCall}
            onChange={handleInputChange}
            options={timeOptions}
            placeholder="Select best time"
            required
          />

          <TextArea label="Anything else we should know?" name="message" value={formData.message} onChange={handleInputChange} rows={3} />

          {/* Premium Consent Card */}
          <div className="pt-4 border-t border-slate-700/50">
            <div className="bg-blue-500/8 border border-blue-400/25 rounded-2xl p-4">
              <div className="flex items-start gap-3 mb-3">
                <SafeIcon icon={FiShield} className="text-blue-400 w-5 h-5 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-blue-300 text-xs font-black tracking-[0.18em] uppercase mb-1">
                    REQUIRED CONSENT *
                  </p>
                  <div className="flex items-start gap-3">
                    <input
                      type="checkbox"
                      name="consentToContact"
                      checked={formData.consentToContact}
                      onChange={handleInputChange}
                      required
                      className="mt-1 w-4 h-4 rounded border-slate-600 bg-slate-800 text-blue-500 focus:ring-blue-500 accent-blue-500 flex-shrink-0"
                    />
                    <label className="text-slate-200 text-sm leading-relaxed">
                      I agree to submit my enquiry to BPA Solutions, and I consent to receive relevant BPA Solutions tips, updates, and service information. I understand I can unsubscribe at any time.
                    </label>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="pt-4">
            <button
              type="submit"
              disabled={isSubmitting}
              className="w-full bg-gradient-to-r from-blue-500 to-purple-600 text-white px-8 py-4 rounded-xl font-bold text-base sm:text-lg hover:from-blue-600 hover:to-purple-700 transition-all duration-200 shadow-xl disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-3"
            >
              {isSubmitting ? (
                <>
                  <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                  Submitting...
                </>
              ) : (
                <>
                  Request My Landing Page Plan
                  <SafeIcon icon={FiIcons.FiSend} />
                </>
              )}
            </button>
          </div>
        </motion.form>
      </div>

      <SubmissionModal stage={submitStage} isVisible={showStageModal} />
    </section>
  );
};

const Input = ({ label, type = 'text', className = '', ...props }) => (
  <div>
    <label className="block text-slate-200 font-semibold text-sm mb-2">{label}{props.required && ' *'}</label>
    <input
      {...props}
      type={type}
      className={`w-full bg-slate-900/80 border border-slate-700/80 rounded-xl px-4 py-3 text-white placeholder-slate-500 focus:ring-2 focus:ring-blue-500/30 focus:border-blue-400 outline-none transition-all h-12 ${className}`}
    />
  </div>
);

const TextArea = ({ label, ...props }) => (
  <div>
    <label className="block text-slate-200 font-semibold text-sm mb-2">{label}</label>
    <textarea
      {...props}
      className="w-full bg-slate-900/80 border border-slate-700/80 rounded-xl px-4 py-3 text-white placeholder-slate-500 focus:ring-2 focus:ring-blue-500/30 focus:border-blue-400 outline-none transition-all resize-y min-h-[96px]"
    />
  </div>
);

export default Payment;